import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Combobox } from '@/components/common/Combobox'
import { useAddAddress, useUpdateAddress } from '../hooks'
import { addressSchema, type AddressFormValues } from '../schema'
import type { AddressDto, AddressKind } from '../types'

interface AddressFormSheetProps {
  customerId: number
  address?: AddressDto | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

const KIND_LABELS: Record<AddressKind, string> = {
  residence: 'Zamieszkania',
  correspondence: 'Korespondencyjny',
  installation: 'Montażu',
}

export function AddressFormSheet({ customerId, address, open, onOpenChange }: AddressFormSheetProps) {
  const isEdit = address != null && address.id != null
  const add = useAddAddress(customerId)
  const update = useUpdateAddress(customerId)

  const form = useForm<AddressFormValues>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      kind: address?.kind ?? 'residence',
      street: address?.street ?? '',
      city: address?.city ?? '',
      postalCode: address?.postalCode ?? '',
      countryCode: address?.countryCode ?? 'PL',
      isPrimary: address?.isPrimary ?? false,
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        kind: address?.kind ?? 'residence',
        street: address?.street ?? '',
        city: address?.city ?? '',
        postalCode: address?.postalCode ?? '',
        countryCode: address?.countryCode ?? 'PL',
        isPrimary: address?.isPrimary ?? false,
      })
    }
  }, [open, address, form])

  const onSubmit = async (values: AddressFormValues) => {
    const payload: AddressDto = {
      id: address?.id ?? null,
      kind: values.kind,
      street: values.street || null,
      city: values.city || null,
      postalCode: values.postalCode || null,
      countryCode: values.countryCode || 'PL',
      isPrimary: values.isPrimary,
      isActive: true,
    }
    try {
      if (isEdit && address?.id != null) {
        await update.mutateAsync({ addressId: address.id, address: payload })
        toast.success('Adres zaktualizowany')
      } else {
        await add.mutateAsync(payload)
        toast.success('Adres dodany')
      }
      onOpenChange(false)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd'
      toast.error(`Nie udało się zapisać: ${msg}`)
    }
  }

  const submitting = add.isPending || update.isPending

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isEdit ? 'Edytuj adres' : 'Dodaj adres'}</SheetTitle>
          <SheetDescription>Wystarczy wypełnić jeden z: ulica, miasto lub kod pocztowy.</SheetDescription>
        </SheetHeader>
        <div className="px-4 pb-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5">
              <FormField
                control={form.control}
                name="kind"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rodzaj adresu</FormLabel>
                    <FormControl>
                      <Combobox<string>
                        value={field.value || null}
                        onChange={v => field.onChange(v ?? 'residence')}
                        options={(Object.keys(KIND_LABELS) as AddressKind[]).map(k => ({ value: k, label: KIND_LABELS[k] }))}
                        placeholder="Wybierz rodzaj..."
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="street"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ulica i numer</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="postalCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Kod pocztowy</FormLabel>
                      <FormControl><Input {...field} placeholder="00-100" /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Miasto</FormLabel>
                      <FormControl><Input {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="countryCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Kraj (ISO)</FormLabel>
                    <FormControl><Input {...field} maxLength={2} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="isPrimary"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-2">
                    <FormControl>
                      <input
                        type="checkbox"
                        className="size-4 accent-primary"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0 cursor-pointer">Główny adres klienta</FormLabel>
                  </FormItem>
                )}
              />
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                  Anuluj
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Zapisywanie...' : isEdit ? 'Zapisz' : 'Dodaj'}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export function useAddressFormSheet(customerId: number) {
  const [open, setOpen] = useState(false)
  const [target, setTarget] = useState<AddressDto | null>(null)
  return {
    openAdd: () => {
      setTarget(null)
      setOpen(true)
    },
    openEdit: (address: AddressDto) => {
      setTarget(address)
      setOpen(true)
    },
    sheet: (
      <AddressFormSheet
        customerId={customerId}
        address={target}
        open={open}
        onOpenChange={setOpen}
      />
    ),
  }
}

export const ADDRESS_KIND_LABELS = KIND_LABELS
